import { useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { SocketStatus } from '../../../data-models/socket-base.data-model';
import { AgentHealthStatusValues } from '../../../schemas/agent-health.schema';
import { HudsonWsService } from '../../../services/HudsonWsService';
import { queryClient } from '../../../services/MaggieQueryClient';
import { getHealthCheck } from '../../../services/queries/AgentQueries';
import { IsReceivingMessagesAtom } from './chatActionHooks';
import { useChatWsStatus } from './useWsChat';

const PollingInterval = 30_000;

export type OverallAgentHealth = 'ok' | 'error' | 'loading';

function deriveOverallStatus(data: AgentHealthStatusValues): OverallAgentHealth {
  const statuses = [data.status, data.database?.status, data.bedrock?.status];
  return statuses.every((s) => s === 'ok') ? 'ok' : 'error';
}

export function useAgentHealthStatus(): OverallAgentHealth {
  const query = useQuery({
    queryKey: ['agentHealthStatus'],
    queryFn: getHealthCheck,
    refetchInterval: PollingInterval,
    select: deriveOverallStatus,
  });

  if (query.isError) {
    return 'error';
  }
  if (query.isPending) {
    return 'loading';
  }
  if (query.data) {
    return query.data;
  }

  // Fallback to error for any unexpected states
  return 'error';
}
export function useReconnectAgent() {
  const [isRetrying, setIsRetrying] = useState(false);
  const setIsReceivingMessages = useSetAtom(IsReceivingMessagesAtom);
  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    setIsReceivingMessages(false);
    await Promise.all([
      HudsonWsService.get()?.reconnect(),
      queryClient.refetchQueries({ queryKey: ['agentHealthStatus'] }),
    ]);
    setIsRetrying(false);
  }, [setIsReceivingMessages]);

  return { handleRetry, isRetrying };
}

export function useCombinedAgentHealth(): OverallAgentHealth {
  const serverHealth = useAgentHealthStatus();
  const clientConnectionStatus = useChatWsStatus();

  if (clientConnectionStatus === SocketStatus.error) {
    return 'error';
  }

  return serverHealth;
}

export function useAgentErrorEffects(status: OverallAgentHealth) {
  const setIsReceivingMessages = useSetAtom(IsReceivingMessagesAtom);

  useEffect(() => {
    if (status === 'error') {
      setIsReceivingMessages(false);
    }
  }, [status, setIsReceivingMessages]);
}
